import React from 'react';
import Icon, { type IconName } from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import type { MaintenanceRequest } from '../../../types';

interface ActiveTasksListProps {
  tasks: MaintenanceRequest[];
  onTaskSelect: (task: MaintenanceRequest) => void;
  onStatusUpdate: (taskId: string, newStatus: string) => void;
  selectedTask: MaintenanceRequest | null;
}

const ActiveTasksList: React.FC<ActiveTasksListProps> = ({ tasks, onTaskSelect, onStatusUpdate, selectedTask }) => {
  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high': return 'text-error border-error bg-error/10';
      case 'medium': return 'text-warning border-warning bg-warning/10';
      case 'low': return 'text-success border-success bg-success/10';
      default: return 'text-muted-foreground border-border bg-muted/10';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'assigned': return 'text-warning bg-warning/10';
      case 'in-progress': return 'text-primary bg-primary/10';
      case 'completed': return 'text-success bg-success/10';
      case 'requires-follow-up': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const formatStatus = (status: string): string => {
    return status.split('-').map((word: string) =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getStatusIcon = (status: string): IconName => {
    switch (status) {
      case 'assigned': return 'Clock';
      case 'in-progress': return 'PlayCircle';
      case 'completed': return 'CheckCircle';
      case 'requires-follow-up': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const handleStatusChange = (taskId: string, newStatus: string): void => {
    onStatusUpdate(taskId, newStatus);
  };

          if (tasks?.length === 0) {
            return (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="ClipboardCheck" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No tasks found</h3>
                <p className="text-muted-foreground">
                  All caught up! Check back later for new assignments.
                </p>
              </div>
            );
          }

          return (
            <div className="space-y-4">
              {tasks?.map((task) => (
                <div
                  key={task.id}
                  className={`bg-card border rounded-lg p-6 card-shadow cursor-pointer transition-all hover:shadow-md ${
                    selectedTask?.id === task.id ? 'ring-2 ring-primary border-primary' : 'border-border'
                  }`}
                  onClick={() => onTaskSelect(task)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{task.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                          {task.priority} priority
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">{task.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="MapPin" size={16} />
                          <span>
                            {task.property_name || (typeof task.property === 'string' ? task.property : task.property?.name || 'Property')}
                            {task.unit ? ` - ${task.unit}` : ""}
                          </span>
                        </div>
                        {task.estimatedTime && task.estimatedTime !== "N/A" && (
                          <div className="flex items-center space-x-1">
                            <Icon name="Clock" size={16} />
                            <span>{task.estimatedTime}</span>
                          </div>
                        )}
                        {task.scheduledDate && (
                          <div className="flex items-center space-x-1">
                            <Icon name="Calendar" size={16} />
                            <span>
                              {typeof task.scheduledDate === 'string' ? task.scheduledDate : task.scheduledDate.toLocaleDateString()}
                              {task.scheduledTime ? ` at ${task.scheduledTime}` : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                        <Icon name={getStatusIcon(task.status)} size={14} className="inline mr-1" />
                        {formatStatus(task.status)}
                      </span>
                    </div>
                  </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {task.tenantContact?.name && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Contact: </span>
                            <span className="text-foreground font-medium">{task.tenantContact.name}</span>
                          </div>
                        )}
                        {task.category && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Category: </span>
                            <span className="text-foreground capitalize">{task.category}</span>
                          </div>
                        )}
                      </div>
                    <div className="flex items-center space-x-2">
                      {task.status === 'assigned' && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Play"
                          iconPosition="left"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(task.id, 'in-progress');
                          }}
                        >
                          Start Task
                        </Button>
                      )}
                      {task.status === 'in-progress' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Pause"
                            iconPosition="left"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, 'assigned');
                            }}
                          >
                            Pause
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            iconName="Check"
                            iconPosition="left"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, 'completed');
                            }}
                          >
                            Complete
                          </Button>
                        </>
                      )}
                      {task.status === 'requires-follow-up' && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="RefreshCw"
                          iconPosition="left"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(task.id, 'assigned');
                          }}
                        >
                          Reschedule
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ExternalLink"
                        iconPosition="left"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskSelect(task);
                        }}
                      >
                        Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        };

        export default ActiveTasksList;
